import "ol/ol.css";
import TileLayer from "ol/layer/Tile";
import ImageLayer from "ol/layer/Image";
import VectorTileLayer from "ol/layer/VectorTile";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import { View, Feature, Map } from "ol";
import { Vector as VectorSource } from "ol/source";
import { WKT, GeoJSON } from "ol/format";
import Point from "ol/geom/Point";
import { fromExtent } from "ol/geom/Polygon";
import { Icon, Stroke, Fill, Style, Text, Circle } from "ol/style";
import { CLASS_UNSELECTABLE, CLASS_CONTROL } from "ol/css";
import VectorLayer from "ol/layer/Vector";
import VectorImageLayer from "ol/layer/VectorImage";
import { defaults as defaultControls, Zoom, Rotate } from "ol/control";
import XYZ from "ol/source/XYZ";
import TileWMS from "ol/source/TileWMS";
import ImageWMS from "ol/source/ImageWMS";
import WMTS from "ol/source/WMTS";
import OSM from "ol/source/OSM";
import BingMaps from "ol/source/BingMaps";
import VectorTileSource from "ol/source/VectorTile";
import Collection from "ol/Collection";
import { Modify, Draw, Snap, defaults as defaultInteractions } from "ol/interaction";
import { getTopLeft } from "ol/extent";
import { get as getProjection } from "ol/proj";
import {assert} from 'ol/asserts';
import {rulesToStyleFunction} from 'ol/render/canvas/style';

import Control from "./ol/Control";
import ControlPanel from "./ol/ControlPanel";
import CityhubCluster from "./ol/source/CityhubCluster";
import { default as Spiderfier_Interaction } from "./ol/interaction/ClusterSpiderfier";
import "./ol/source/TileWMS";
// import WebGLVectorLayer from 'ol/layer/WebGLVector';
import WebGLVectorLayer from './ol/layer/WebGLVector';
import {setAuthorizationToken, xhr} from "./ol/featureloader";
import { DEFAULT_DPI, getScaleFromResolution } from "./shared/catalog/utils/MapUtils";
import { getLayerId } from "./shared/catalog/utils/LayersUtils";
import { getEPSGCode } from "./shared/catalog/utils/CoordinatesUtils";
import { isString, isArray, isObject, isJson, isNullOrUndefined, formatString, uniqueId, getUrlWithoutParameters, dotNotation } from "@config/utils";
import { detectMobile } from "@config/browser";

// setAuthorizationToken();

const _propertiesDefault = {
    '#drawTemporaryFeatureTimeout': null,

    '#target': null,
    '#map': null,
    '#initialExtent': null,
    '#center': [],
    '#zoom': 3,
    '#features': null,
    '#maxZoom': 10,
    '#layer': {},
    '#source': {},
    '#geomBoundary': null,
    '#selectedFeature': null,
    '#controls': {},
    // #interactions': {}
    '#panels': {},

    '#layers': {
        features: {
            layer: "lyr_feat",
            source: "vs_feat",
            cluster: "cl_new"
        },
        new_feature: {
            layer: "lyr_new",
            source: "vs_new"
        },
        draw_feature: {
            layer: "lyr_draw",
            source: "vs_draw"
        },
        style: {
            highlight: {
                timeout: 6000,
                duration: 1500
            },
            mark: {
                zindex: 9,
                scale: 0.5,
                scaleSelected: 1
            },
            cluster: {
                zindex: 8,
                scale: 1
            }
        },
        zindex: {
            cluster: 200,
            new_layer: 1000,
            search_layer: 199
        }
    },

    '#cluster': {
        clusterDistance: 100,
        loading: true
    },

    '#config': {
        layers: {},
        cluster: {},
        style: {}
    },
};

export class MapClass {
    #drawTemporaryFeatureTimeout = null;

    #target = null;
    #map = null;
    #initialExtent = null;
    #center = [];
    #zoom = null;
    #features = null;
    #maxZoom = null;
    #layer = {};
    #source = {};
    #geomBoundary = null;
    #selectedFeature = null;
    #controls = {};
    // #interactions = {}
    #panels = {};

    #layers = {};

    #cluster = {};

    #config = {};

    #isMobile = false;

    #wmsStandardParams = [
        'SERVICE',
        'VERSION',
        'REQUEST',
        'FORMAT',
        'SRS',
        'CRS',
        'LAYERS',
        'STYLES',
        'BBOX',
        'TRANSPARENT',
        'WIDTH',
        'HEIGHT',
        'CQL_FILTER',
    ];

    constructor(config = {}) {
        this.#isMobile = detectMobile();
        Object.assign(this, _propertiesDefault);

        config = JSON.parse(JSON.stringify(config));
        this.#config = config || { layers: this.#layers, cluster: this.#cluster, style: this.#layers.style };
    }

    setAuthorizationToken(token) {
        setAuthorizationToken(token);
        return this;
    }

    recreateMap() {
        this.destroyMap();
        this.reset();
    }

    reset() {
        Object.assign(this, _propertiesDefault);
    }

    getLayersProperties() {
        return this.#config.layers || {};
    }

    getStyleProperties() {
        return this.#config.style || {};
    }

    getInteractionsProperties() {
        return this.#config.interactions || {};
    }

    getClusterProperties() {
        return this.#cluster;
    }

    //////////////////////////////////////////////////////////////////
    // Interactions
    // addInteraction(id, typeClass, ...options){
    //     let interaction = this.#interactions[id];
    //     if(!interaction){
    //         switch(typeClass){
    //             case 'draw':
    //                 interaction = new Draw({
    //                     source: options.source,
    //                     type: options.type,
    //                 });
    //                 break;
    //         }
    //         this.#interactions[id] = interaction;
    //     }
    //     return interaction;
    // }

    getInteractions() {
        let map = this.getOLMap();
        return map ? map.getInteractions() : [];
    }

    addInteraction(interaction) {
        let map = this.getOLMap();
        if (interaction && map) {
            map.addInteraction(interaction);
        }
    }

    removeInteraction(interaction) {
        let map = this.getOLMap();
        if (interaction && map) {
            map.removeInteraction(interaction);
        }
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que manipula as layers
    setLayer(name, layer) {
        return (this.#layer[name] = layer);
    }
    getLayer(name) {
        return this.#layer[name];
    }
    addLayer(layer) {
        let map = this.getOLMap();
        return map ? map.addLayer(layer) : null;
    }
    removeLayer(layer, name = null) {
        let map = this.getOLMap();
        if (map) {
            layer.setSource(null);
            map.removeLayer(layer);
        }
        if (name) {
            this.#layer[name] = null;
        }
    }
    getLayers() {
        let map = this.getOLMap();
        return map ? map.getLayers() : new Collection();
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que manipula os sources
    setSource(name, source) {
        return (this.#source[name] = source);
    }
    getSource(name) {
        if (typeof this.#source[name] !== "undefined") {
            return this.#source[name];
        } else {
            return null;
        }
    }
    getSources() {
        return this.#source;
    }
    removeSource(name, fast = false) {
        let source = this.#source[name];
        if (source) {
            source.clear(fast);
        }
        this.#source[name] = null;
    }
    clearSource(name, fast = false) {
        let source = this.#source[name];
        if (source) {
            source.clear(fast);
        }
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que manipula as features
    setSelectedFeature(feature) {
        this.#selectedFeature = feature;
    }

    getSelectedFeature(name) {
        return this.#selectedFeature;
    }

    unHighlightFeature(feature, layer = "features") {
        // unselect
        if (feature) {
            let options = this.getLayersProperties();
            options = options[layer];
            let style = feature.getStyle();
            let img = style?.getImage() ?? null;
            if (img) {
                img.setScale(options.scale);
            }
            feature.setStyle(feature.getStyle());
            this.setSelectedFeature(null);
            $("#map-tooltip").css("display", "none");
        }
    }

    highlightFeature(id, source = null, layer = "features", zoomto = true) {
        source = source ? source || "" : "";
        let selFeature = this.getSelectedFeature();
        if (selFeature != null) {
            this.unHighlightFeature(selFeature);
        }

        if (id) {
            let options = this.getLayersProperties();
            options = options[layer];
            let mapSource = this.getSource(options.source + source);
            if (mapSource) {
                let feature = mapSource.getFeatureById(id);
                if (feature && feature.get("visible")) {
                    let style = feature.getStyle();
                    let img = style?.getImage() ?? null;
                    if (img) {
                        img.setScale(options.scaleSelected);
                    }
                    feature.setStyle(style);
                    this.setSelectedFeature(feature);

                    setTimeout(() => {
                        this.unHighlightFeature(feature);
                    }, options.timeout);

                    if (zoomto) {
                        let geom = feature.clone().getGeometry();
                        geom.scale(options.zoomScale);
                        this.zoomToExtent(geom.getExtent(), options.duration);
                    }
                }
            }
        }
    }

    drawTemporaryFeature(params = {}) {
        const { data, source, style, padding, layersProperties, idProperty, geomProperty, timeout, preserveGeometry } = Object.assign(
            {
                data: null,
                source: "highlight",
                style: null,
                padding: [0, 0, 0, 0],
                layersProperties: null,
                idProperty: null,
                geomProperty: null,
                timeout: null,
                preserveGeometry: false
            },
            params
        );

        if (data) {
            let id = idProperty ? data[idProperty] : data.id || data.code || uniqueId();
            let geomKeys = [geomProperty, "geojson", "geom", "position"].filter(f => f);
            let geomKey = geomKeys.find(k => k in data);
            let geometries = data[geomKey];
            if (!geometries) return;
            if (!isArray(geometries)) geometries = [geometries];

            let properties = Object.assign({}, data);
            [...geomKeys, "geomProperty"].forEach(k => delete properties[k]);

            let layerStyle = style;
            if (!layerStyle) {
                let styleOptions = this.getStyleProperties();
                if (styleOptions && source in styleOptions) {
                    styleOptions = styleOptions[source];
                    let imgSrc = styleOptions?.image?.src;
                    let imageIcon = imgSrc
                        ? this.createIcon({
                              src: imgSrc,
                              anchor: [0.5, 1],
                              scale: 1
                          })
                        : null;
                    let circleIcon = {
                        fill: {
                            color: styleOptions.image.colorBack
                        },
                        stroke: {
                            color: styleOptions.image.color,
                            width: styleOptions.image.width
                        },
                        radius: styleOptions.image.radius
                    };
                    layerStyle = [
                        this.createStyle({
                            image: imageIcon,
                            circle: circleIcon,
                            stroke: {
                                color: styleOptions.stroke.colorBack,
                                width: styleOptions.stroke.width + parseInt(styleOptions.stroke.width / 3)
                            }
                        }),
                        this.createStyle(
                            Object.assign(
                                {},
                                {
                                    stroke: {
                                        color: styleOptions.stroke.color,
                                        width: styleOptions.stroke.width
                                    }
                                },
                                this.createStyle({
                                    cursor: "pointer",
                                    image: imageIcon,
                                    circle: circleIcon
                                })
                            )
                        )
                    ];
                }
            }

            let options = layersProperties ? layersProperties : this.getLayersProperties();
            if (!options || !source in options) return;

            options = options[source];
            let zoomDuration = options.duration || 1000;
            let zoomScale = options.zoomScale || 1.5;
            let _timeout = preserveGeometry ? 0 : timeout ?? options.timeout ?? 4000;
            let layerZindex = options.zindex || 1000;
            let layerId = options.layer;
            let sourceId = options.source;

            let _source = this.getSource(sourceId);
            let _layer = this.getLayer(layerId);
            if (!_layer) {
                if (!_source) {
                    _source = this.createVectorSource({
                        features: []
                    });
                    this.setSource(sourceId, _source);
                }
                _layer = this.createVectorLayer({
                    source: _source,
                    zIndex: layerZindex
                });
                this.setLayer(layerId, _layer);
                this.addlayerToMap({ layer: _layer },true);
            }
            this.clearSource(sourceId);

            geometries.forEach((geometry, index) => {
                let _id = geometry[idProperty] || geometry.id || (geometries.length > 1 ? uniqueId() : id);
                let geom = geometry;
                let keys = Object.keys(geometry);
                if (isObject(geometry) && keys.filter(value => geomKeys.includes(value)).length) {
                    // id = geometry.id || id;
                    geom = geometry[geomKeys.find(k => k in geometry)];
                }

                if (geom) {
                    let style = geometry.style || layerStyle;
                    let feature = null;
                    if (isJson(geom)) {
                        feature = this.createFeatureFromGeojson({
                            id: _id,
                            geom: geom,
                            properties: properties,
                            style: style
                        });
                    } else {
                        feature = this.createFeatureFromWKT({
                            id: _id,
                            data: geom,
                            style: style
                        });
                    }
                    if (feature) {
                        _source.addFeature(feature);
                    }
                }
            });

            if (_source.getFeatures().length > 0) {
                let geom = fromExtent(_source.getExtent());
                if (geom) {
                    geom.scale(zoomScale);
                    this.zoomToExtent(geom.getExtent(), zoomDuration, padding);
                    if (_timeout > 0) {
                        if (this.#drawTemporaryFeatureTimeout) clearTimeout(this.#drawTemporaryFeatureTimeout);
                        this.#drawTemporaryFeatureTimeout = setTimeout(() => {
                            this.clearSource(sourceId);
                        }, _timeout);
                    }
                }
            }
        }
    }

    clearTemporaryFeature(params = {}) {
        const { source } = { ...{ source: "highlight" }, ...params };
        let options = this.getLayersProperties();
        if (options && source in options) {
            options = options[source];
            let sourceId = options.source;
            this.clearSource(sourceId);
        }
    }

    animateToFeature(coords, zoom, callback = null) {
        this.getOLMapView().animate(
            {
                center: coords,
                zoom: zoom != null ? zoom : this.#maxZoom,
                duration: duration
            },
            callback
        );
    }

    flyTo(location, zoomTo, done, duration = 2000) {
        //var zoom = zoom != null ? zoom : this.#maxZoom
        const zoom = this.getOLMapView().getZoom();
        let parts = 2;
        let called = false;
        function callback(complete) {
            --parts;
            if (called) {
                return;
            }
            if (parts === 0 || !complete) {
                called = true;
                done(complete);
            }
        }
        this.getOLMapView().animate(
            {
                center: location,
                duration: duration
            },
            callback
        );

        this.getOLMapView().animate(
            {
                zoom: zoom - 1,
                duration: duration / 2
            },
            {
                zoom: zoomTo ?? zoom,
                duration: duration / 2
            },
            callback
        );
    }

    setGeomBoundary(geom) {
        if (isJson(geom)) {
            let format = new GeoJSON();
            let feature = format.readFeature(geom);
            if (feature) {
                this.#geomBoundary = feature.getGeometry();
            }
        }
    }

    getGeomBoundary() {
        return this.#geomBoundary;
    }

    checkIntersectBoundary(coords) {
        let boundary = this.getGeomBoundary();
        return boundary ? boundary.intersectsCoordinate(coords) : true;
    }

    animateToLatLong(coords, zoom, duration = 1000, done) {
        function callback(complete) {
            if (typeof done === "function") {
                done(complete);
            }
        }
        this.getOLMapView().animate(
            {
                center: coords,
                zoom: zoom != null ? zoom : this.#maxZoom - 1,
                duration: duration
            },
            callback
        );
    }

    animateToExtent(extent) {
        var map = this.getOLMap();
        if (map) {
            if (Object.prototype.toString.call(extent) === "[object Array]") {
                map.getView().fit(extent, {
                    maxZoom: this.#maxZoom - 1,
                    nearest: false
                });
            }
        }
    }

    zoomToExtent(extent, duration = 1000, padding = [0, 0, 0, 0], params = {}) {
        var map = this.getOLMap();
        if (map) {
            let options = {
                duration: duration, // The duration of the animation in milliseconds
                padding: padding, // Padding (in pixels) to be cleared inside the view. Values in the array are top, right, bottom and left padding.
                ...params
            };
            map.getView().fit(extent, options);
        }
    }

    getFeatures() {
        return this.#features;
    }

    setFeatures(features) {
        this.#features = features;
    }

    createFeature(config) {
        let feature = new Feature(new Point([parseFloat(config.longitude), parseFloat(config.latitude)]));
        feature.setId(config.id);
        feature.setProperties(Object.assign({}, config.properties || {}), true);
        feature.setStyle(config.style);
        Object.seal(feature);
        return feature;
    }

    createFeatureFromWKT(config) {
        let format = new WKT();
        let srid = null;
        let data = config.data || config.geom;
        let parts = data && isString(data) ? data.split(";") : [];
        if (parts.length > 0) {
            if (parts.length == 2) {
                srid = parts[0];
            }
            let feature = format.readFeature(parts[parts.length - 1]);
            if (feature) {
                if (srid) {
                    var map = this.getOLMap();
                    feature.getGeometry().transform(srid.toUpperCase().replace("SRID=", "EPSG:"), map.getView().getProjection().getCode());
                }
                if (config.id) {
                    feature.setId(config.id);
                }
                if (config.properties) {
                    feature.setProperties(config.properties, true);
                }
                if (config.style) {
                    feature.setStyle(config.style);
                }
                Object.seal(feature);
                return feature;
            }
        }
        return null;
    }

    createFeatureFromGeojson(config = {}) {
        let geom = config.geom ?? config.geometry;
        if (!geom) {
            return null;
        }
        let format = new GeoJSON();
        let feature = format.readFeature(geom, {
            dataProjection: config?.dataProjection ?? this.getProjection().getCode(),
            featureProjection: this.getProjection().getCode()
        });
        if (feature) {
            if (config.id) {
                feature.setId(config.id);
            }
            if (config.properties) {
                feature.setProperties(config.properties, true);
            }
            if (config.style) {
                feature.setStyle(config.style);
            }
            Object.seal(feature);
            return feature;
        }
        return null;
    }

    createCollection(features) {
        return new Collection(features);
    }

    createModify(config) {
        return new Modify(config);
    }

    createSnap(config) {
        return new Snap(config);
    }

    createDraw(config) {
        return new Draw(config);
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que manipula o target da view
    getTarget() {
        return this.#target;
    }
    setTarget(target) {
        this.#target = target;
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que adiciona os controllers

    getPanel(side, options = {}) {
        let panel = this.#panels[side];
        if (!panel) {
            let map = this.getOLMap();
            if (map != null) {
                if (!options.target) {
                    options.target = map.getOverlayContainerStopEvent();
                    // options.target = map.getOverlayContainer();
                }
                panel = new ControlPanel({ ...options, side: side, addCssProperty: true });
                this.#panels[side] = panel;
                map.addControl(panel);
            }
        } else {
            if (options.subposition) {
                panel.addSubElement(options.subposition);
            }
        }
        return panel;

        // let subpanel = panel[direction];
        // if(!subpanel){
        //     switch(side) {
        //         case POSITION.TOP_LEFT:
        //         case POSITION.TOP_RIGHT:
        //             if(direction == Direction.VERTICAL){
        //                 let otherdir = Direction.HORIZONTAL;
        //                 let otherpanel = panel[otherdir];
        //                 if(!otherpanel){
        //                     let otherPanel = new ControlPanel({...options, 'target': panel.element, 'direction': otherdir});
        //                     this.getOLMap().addControl(otherPanel);
        //                     this.#panels[side][otherdir] = otherPanel;
        //                 }
        //             }
        //             subpanel = new ControlPanel({...options, 'target': panel.element, 'direction': direction});
        //             this.getOLMap().addControl(subpanel);
        //             this.#panels[side][direction] = subpanel;
        //             break;
        //         case POSITION.BOTTOM_LEFT:
        //         case POSITION.BOTTOM_RIGHT:
        //             if(direction == Direction.VERTICAL){
        //                 let otherdir = Direction.HORIZONTAL;
        //                 let otherpanel = panel[otherdir];
        //                 if(!otherpanel){
        //                     let otherPanel = new ControlPanel({...options, 'target': panel.element, 'direction': otherdir});
        //                     this.getOLMap().addControl(otherPanel);
        //                     this.#panels[side][otherdir] = otherPanel;
        //                 }
        //             }
        //             subpanel = new ControlPanel({...options, 'target': panel.element, 'direction': direction});
        //             this.getOLMap().addControl(subpanel);
        //             this.#panels[side][direction] = subpanel;
        //             break;
        //     }

        // }
        // return subpanel;
    }

    addControlOL(ctrl) {
        if (ctrl) {
            let map = this.getOLMap();
            if (map != null) {
                map.addControl(ctrl);
                map.dispatchEvent(new Event("control-added", ctrl));
                return ctrl;
            }
        }
        return null;
    }

    addControl(side, button) {
        // let distanceTop = 0;
        // let distanceHeight = 0;

        if (typeof this.#controls[side] == "undefined") {
            this.#controls[side] = [button];
        } else {
            this.#controls[side].push(button);
        }

        // if(side == 'left')
        // {
        //     distanceTop = this.#controls[side] && this.#controls[side].length>1 ? 158 : 129;
        //     distanceHeight = 57;
        // }
        // else
        // {
        //     distanceTop = 16;
        //     distanceHeight = 57;
        // }

        // button.element.classList.add("ol-cityhub", "ol-side-"+side);
        // button.element.style.top = distanceTop + (distanceHeight*(this.#controls[side].length-1))+'px';
        // button.element.setAttribute('style', 'top: ' + (distanceTop + (distanceHeight*(this.#controls[side].length-1))) + 'px !important;');
        let panel = this.getPanel(side);
        if (panel) {
            panel.addElement(button.element);
            return button;
        }
        return null;
    }

    getControls() {
        let _controls = new Collection();
        _controls.extend(this.getMapControls().getArray());
        Object.keys(this.#controls).forEach(k => {
            _controls.extend(this.#controls[k]);
        });
        return _controls;
    }

    getMapControls() {
        let _map = this.getOLMap();
        if (_map != null) {
            return _map.getControls();
        }
        return new Collection();
    }

    getOverlays() {
        let _map = this.getOLMap();
        if (_map != null) {
            return _map.getOverlays();
        }
        return new Collection();
    }

    addOverlay(overlay) {
        let _map = this.getOLMap();
        if (_map != null && overlay) {
            _map.addOverlay(overlay);
        }
    }

    removeOverlay(overlay) {
        let _map = this.getOLMap();
        if (_map != null && overlay) {
            _map.removeOverlay(overlay);
        }
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que manipula a view
    getInitialExtent() {
        return this.#initialExtent;
    }

    setInitialExtent(initialExtent) {
        this.#initialExtent = initialExtent;
    }

    getExtent() {
        if (this.getOLMapView() != null) {
            return this.getOLMapView().calculateExtent();
        } else {
            return null;
        }
    }

    getCenter() {
        if (this.getOLMapView() != null) {
            return this.getOLMapView().getCenter();
        } else {
            return this.#center;
        }
    }

    setCenter(center) {
        this.#center = center;
    }

    resetMap() {
        this.getOLMapView().animate({
            center: this.#center,
            zoom: this.#zoom
        });
    }

    getProjection() {
        var view = this.getOLMapView();
        if (view) {
            return view.getProjection();
        }
        return null;
    }

    getProjectionCode() {
        return this.getProjection()?.getCode() ?? null;
    }

    getProjectionFromCode(code) {
        return getProjection(code);
    }

    getResolutions() {
        let map = this.getOLMap();
        if (map) {
            return map.getView().getResolutions();
        }
        return null;
    }

    getResolution() {
        let map = this.getOLMap();
        if (map) {
            // return map.getView().getResolution();
            let view = map.getView();
            return view.getResolutionForZoom(view.getConstrainedZoom(view.getZoom()));
        }
        return null;
    }

    getMinResolution() {
        let map = this.getOLMap();
        if (map) {
            return map.getView().getMinResolution();
        }
        return null;
    }

    getMaxResolution() {
        var map = this.getOLMap();
        if (map) {
            return map.getView().getMaxResolution();
        }
        return null;
    }

    getResolutionForZoom(zoom) {
        let view = this.getOLMapView();
        return view ? view.getResolutionForZoom(zoom) : view.getResolution();
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que manipula o zoom
    getZoom() {
        let view = this.getOLMapView();
        if (view != null) {
            return view.getZoom();
        } else {
            return this.#zoom;
        }
    }

    setZoom(zoom, duration = 1000) {
        let view = this.getOLMapView();
        if (view != null) {
            if (!!duration) {
                view.animate({
                    zoom: zoom,
                    duration: duration
                });
            } else {
                view.setZoom(zoom);
            }
        }
    }

    getMinZoom() {
        let view = this.getOLMapView();
        return view ? view.getMinZoom() : 0;
    }

    getMaxZoom() {
        let view = this.getOLMapView();
        return view ? view.getMaxZoom() : 0;
    }

    getZoomForResolution(resolution) {
        let view = this.getOLMapView();
        return view ? view.getZoomForResolution(resolution) : view.getZoom();
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que manipula o objeto MAP - PRIVADO
    getOLMap() {
        return this.#map;
    }

    setOLMap(map) {
        this.#map = map;
    }

    getOLMapView() {
        var map = this.getOLMap();
        if (map) {
            return map.getView();
        }
        return null;
    }

    mapRender() {
        var map = this.getOLMap();
        if (map) {
            map.render();
        }
    }

    fitMap(scale = null) {
        let map = this.getOLMap();
        if (map) {
            let initialExtent = this.getInitialExtent();
            if (initialExtent) {
                let extent = null;
                if (isArray(initialExtent)) {
                    extent = Object.assign({}, ...initialExtent);
                } else if (isObject(initialExtent)) {
                    extent = [initialExtent.minx, initialExtent.miny, initialExtent.maxx, initialExtent.maxy];
                }
                if (extent) {
                    if (scale) {
                        let geom = fromExtent(extent);
                        if (geom) {
                            geom.scale(scale);
                            extent = geom.getExtent();
                        }
                    }
                    map.getView().fit(extent);
                }
            }
        }
    }

    fitToExtent(extent) {
        var map = this.getOLMap();
        if (map) {
            if (Object.prototype.toString.call(extent) === "[object Array]") {
                map.getView().fit(extent);
            }
        }
    }

    scaleExtent(extent, scale = 1) {
        let geom = fromExtent(extent);
        if (geom) {
            geom.scale(scale);
            return geom.getExtent();
        }
        return extent;
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que cria e retorn um icone
    createIcon(config) {
        let icon = new Icon({
            anchor: config.anchor || [0.5, 0.5],
            anchorOrigin: config.anchorOrigin || "top-left",
            anchorXUnits: config.anchorXUnits || "fraction",
            anchorYUnits: config.anchorYUnits || "fraction",
            //img             : iconImage,
            src: config.src,
            //size            : config.size || [80, 80],
            //imgSize         : config.imgSize || [80, 80],
            scale: config.scale || 1.0,
            zIndex: config.zIndex || 9,
            rotation: config.rotation || 0
        });
        return icon;
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que cria e retorna um estilo
    createStyle(config) {
        let image = config.image;
        if (image) {
            image = image instanceof Icon ? image : new Icon(image);
        } else if (config.circle) {
            image = new Circle({
                fill: config.circle.fill ? new Fill(config.circle.fill) : null,
                stroke: config.circle.stroke ? new Stroke(config.circle.stroke) : null,
                radius: config.circle.radius || 5
            });
        }
        let style = new Style({
            stroke: config.stroke ? new Stroke(config.stroke) : null,
            fill: config.fill ? new Fill(config.fill) : null,
            image: image ? image : null,
            text: config.text ? (config.text instanceof Text ? config.text : new Text(config.text)) : null,
            zIndex: config.zIndex || 9
        });
        Object.seal(style);
        return style;
    }

    createStyleExpressions(config) {
        let style = config;
        if(!isArray(style)) {
            style = [style];
        }

        style = style.reduce((accumulator, item) => {
            let _res = JSON.parse(JSON.stringify(item));

            if(Object.hasOwn(_res, 'style')) {
                _res.style = dotNotation(_res.style, {}, [], '-', true, true, true, '-');
            }else{
                _res = dotNotation(_res, {}, [], '-', true, true, true, '-');
            }
            accumulator.push(_res);
            return accumulator;
        }, []);

        return style;
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que criar e retorna um Vector Source
    createVectorSource(config) {
        if(!config.loader) {
            if (config.url !== undefined) {
                assert(config.format, '`format` must be set when `url` is set');
                config.loader = xhr(config.url, config.format);
            }
        }

        let source = new VectorSource({
            ...config
        });
        Object.seal(source);
        return source;
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que cria e retorna um Cluster
    createCluster(config) {
        let cluster = new CityhubCluster({
            map: this.getOLMap(),
            // disableClusteringAtZoom: 7,
            distance: config.distance != undefined ? config.distance : 200,
            source: config.source,
            filters: config.filters,
            filterFunc: config.filterFunc
        });
        Object.seal(cluster);
        return cluster;
    }
    // eventChangeResolution(config)
    // {
    //     this.getOLMap().getView().on('change:resolution', function(evt)
    //     {
    //         var view = evt.target
    //         var maxZoom = view.getMaxZoom()
    //         var source = config.source
    //         var distance = source.getDistance()

    //         if(view.getZoom() >= maxZoom)
    //         {
    //             source.setDistance(0)
    //         }
    //         else if(view.getZoom() < maxZoom && distance === 0)
    //         {
    //             source.setDistance(config.distance || 200)
    //         }
    //     }, this.getOLMap())
    // }

    getScaleForResolution = function (resolution, dpi = DEFAULT_DPI, projection, opt_round = true) {
        projection = projection && isObject(projection) ? projection.getCode() : projection;
        return getScaleFromResolution(projection ?? this.getProjectionCode(), resolution ?? this.getResolution(), dpi ?? DEFAULT_DPI, opt_round);
        // const _resolution = resolution ?? this.getResolution();
        // const _projection = projection ?? this.getProjection();
        // const _dpi = dpi ?? DEFAULT_DPI;
        // const mpu = _projection.getMetersPerUnit();
        // const inchesPerMeter = 1000 / 25.4;
        // let scale = parseFloat(_resolution.toString()) * mpu * inchesPerMeter * _dpi;
        // if (opt_round) {
        //     scale = Math.round(scale);
        // }
        // return scale;
    };

    //////////////////////////////////////////////////////////////////
    applyDefaultParamsLayer(config) {
        let res = {
            id: config.id,
            type: config.type,
            url: config.url,
            queryable: !!config.queryable,
            featureable: config.featureable ?? null,
            by_default: config.by_default || false,
            behavior: config.behavior || null,
            img: config.img || null,
            code: config.code,
            name: config.name,
            visible: config.visible,
            minZoom: config.minZoom ? parseInt(config.minZoom) : undefined,
            maxZoom: config.maxZoom ? parseInt(config.maxZoom) : undefined,
            opacity: config.opacity ? parseFloat(config.opacity) : 1,
            zIndex: config.zIndex,
            bbox: config.bbox || null,
            featureInfo: config.featureInfo || {},
            tools: config.tools || null,
            legend_url: config.legend_url || null,
            attributions: this.__getAttributions(config)
        };
        return res;
    }

    applyDefaultParamsSourceLayer(config) {
        let res = {
            ...config,
            attributions: this.__getAttributions(config)
        };
        return res;
    }

    __getAttributions(config) {
        const creditsToAttribution = ({ imageUrl, link, title }) => {
            // TODO: check if format is valid for an img (svg, for instance, may not work)
            const html = imageUrl ? `<img src="${imageUrl}" ${title ? `title="${title}"` : ``}>` : title;
            return link && html ? `<a href="${link}" target="_blank">${html}</a>` : html;
        };

        let attributions = [];
        let _attributions = config.attributions ? config.attributions : config.attribution;
        if (!isNullOrUndefined(_attributions)) {
            if (isString(_attributions)) {
                _attributions = _attributions
                    .trim()
                    .split("|")
                    .map(item => item.trim());
            }

            if (!isArray(_attributions)) {
                attributions = [_attributions];
            } else {
                attributions = _attributions;
            }
        }

        attributions = attributions.map(item => {
            if (isObject(item)) {
                return creditsToAttribution(item);
            }
            return item;
        });
        return attributions.filter(Boolean); //.join(" | ");
    }

    // Metodo que cria e retorna um vectorLayer
    createVectorLayer(config, renderModeImage = false, renderAsWebGL = false) {
        if (typeof config.visible === "undefined") {
            config.visible = true;
        }

        let styleCluster = null;
        if (config.cluster) {
            styleCluster = this.createStyle({
                image: config.clusterIcon,
                text: config.clusterText
                    ? config.clusterText
                    : {
                          placement: "point",
                          textAlign: "center",
                          text: 0,
                          font: "11px sans-serif",
                          fill: new Fill({
                              color: config.clusterTextColor || "#fff"
                          })
                      }
            });
        }

        let _style = config.style;
        if(_style && (isObject(_style) || isArray(_style))) { // For StyleLike case
            _style = rulesToStyleFunction(isObject(_style) ? [_style] : _style);
            // let _vectorLayer = new VectorLayer({ style: isObject(_style) ? [_style] : _style })
            // if (_vectorLayer) {
            //     _style = _vectorLayer.getStyleFunction();
            //     _vectorLayer = null;
            // }
        }

        let layerConfig = {
            ...config,
            ...{
                declutter: config.declutter != undefined ? config.declutter : false,
                source: config.source || null,
                visible: config.visible != undefined ? config.visible : true,
                zIndex: config.zIndex,
                // Layer style. When set to null, only features that have their own style will be rendered.
                style:
                    config.cluster || !_style
                        ? (feature, resolution) => {
                            if (config.cluster) {
                                let feats = feature.get("features") || [];
                                let size = feats.length;
                                if (size > 1) {
                                    let style = styleCluster.clone();
                                    style.getText().setText(size.toString());
                                    return style;
                                } else if (size == 1) {
                                    feature = feats[0];
                                }
                            }

                            let _featStyle = feature.getStyle() ?? _style;
                            if (typeof _featStyle === "function") {
                                _featStyle = _featStyle(feature, resolution);
                            }
                            return _featStyle;
                        }
                        : _style
            }
        };

        let layer = renderAsWebGL ? new WebGLVectorLayer(layerConfig) : (renderModeImage ? new VectorImageLayer(layerConfig) : new VectorLayer(layerConfig));
        Object.seal(layer);
        return layer;
    }

    createWebGLVectorLayer(config) {
        return this.createVectorLayer(config, false, true);
    }


    //////////////////////////////////////////////////////////////////

    createTMSLayer(config) {
        return null;
        // return  new TileLayer({
        //     ...this.applyDefaultParamsLayer(config),
        //     type: 'base',
        //     source: new XYZ({
        //         url: `${config.url.replace(/\/$/, '')}/${config.version !== undefined ? config.version : '1.0.0'}/${config.param_layers || config.layer}/{z}/{x}/{y}.${config.param_format || config.format}`,
        //         //'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        //         tileGrid: new WMTSTileGrid({
        //             origin: [mapExtent[0], mapExtent[1]],
        //             resolutions: resolutions,
        //             matrixIds: matrixIds
        //         })
        //     })
        // });
    }

    createTileLayer(config) {
        if (!config.params) {
            let key,
                keys = Object.keys(config);
            let n = keys.length;
            let params = {};
            while (n--) {
                key = keys[n];
                if (key.toLowerCase().lastIndexOf("param_", 0) === 0 && !["param_xyz", "param_tiled"].includes(key.toLowerCase())) {
                    params[key.toUpperCase().replace(/param_/i, "")] = config[key];
                }
            }
            config.params = params;
        }

        let params = this.applyDefaultParamsLayer(config);
        if(params.url) {
            params.url = getUrlWithoutParameters(params.url, this.#wmsStandardParams);
        }

        let sourceParams = this.applyDefaultParamsSourceLayer(config);
        if(sourceParams.url) {
            sourceParams.url = getUrlWithoutParameters(sourceParams.url, this.#wmsStandardParams);
        }

        let layer = new TileLayer({
            ...params,
            source: new TileWMS({ ...sourceParams })
        });

        Object.seal(layer);
        return layer;
    }

    createImageLayer(config) {
        let params = this.applyDefaultParamsLayer(config);
        if(params.url) {
            params.url = getUrlWithoutParameters(params.url, this.#wmsStandardParams);
        }

        let sourceParams = this.applyDefaultParamsSourceLayer(config);
        if(sourceParams.url) {
            sourceParams.url = getUrlWithoutParameters(sourceParams.url, this.#wmsStandardParams);
        }

        let layer = new ImageLayer({
            ...params,
            source: new ImageWMS({ ...sourceParams })
        });
        Object.seal(layer);
        return layer;
    }

    createXYZLayer(config) {
        let layer = new TileLayer({
            ...this.applyDefaultParamsLayer(config),
            source: new XYZ({ ...this.applyDefaultParamsSourceLayer(config) })
        });
        Object.seal(layer);
        return layer;
    }

    createWMTSLayer(config) {
        var projection = getProjection(config.srs);
        var projectionExtent = projection.getExtent();

        let layer = new TileLayer({
            ...this.applyDefaultParamsLayer(config),
            source: new WMTS({
                ...this.applyDefaultParamsSourceLayer(config),
                requestEncoding: config.requestEncoding !== undefined ? config.requestEncoding : "KVP",
                url: config.url,
                layer: config.layer,
                matrixSet: config.matrixSet,
                format: config.format,
                projection: projection,
                tileGrid: new WMTSTileGrid({
                    origins: config.origins,
                    origin: !config.origins ? getTopLeft(projectionExtent) : undefined,
                    resolutions: config.resolutions,
                    matrixIds: config.matrixIds,
                    sizes: config.sizes ? config.sizes : undefined,
                    tileSizes: config.tileSizes ? config.tileSizes : undefined
                }),
                style: config.style ? config.style : "default",
                wrapX: true
            })
        });
        Object.seal(layer);
        return layer;
    }

    createOSMLayer(config) {
        let layer = new TileLayer({
            ...this.applyDefaultParamsLayer(config),
            source: new OSM({ ...this.applyDefaultParamsSourceLayer(config) })
        });
        Object.seal(layer);
        return layer;
    }

    createBingMapsLayer(config) {
        let layer = new TileLayer({
            ...this.applyDefaultParamsLayer(config),
            source: new BingMaps({ ...this.applyDefaultParamsSourceLayer(config) })
        });
        Object.seal(layer);
        return layer;
    }

    createVectorTileLayer(config) {
        let layer = new VectorTileLayer({
            ...this.applyDefaultParamsLayer(config),
            source: new VectorTileSource({ ...this.applyDefaultParamsSourceLayer(config) })
        });
        Object.seal(layer);
        return layer;
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que adiciona uma layer no mapa
    addlayerToMap(config, updateVectorLayers = false) {
        if (typeof config.layer !== "undefined") {
            config.layers = new Array(1);
            config.layers[0] = config.layer;
        }
        let map = this.getOLMap();
        if (map) {
            config.layers.map(item => {
                map.addLayer(item);
            });
            if (updateVectorLayers) {
                let layers = map.getLayers().getArray();
                let maxZIndex = Math.max.apply(
                    Math,
                    layers.map(function (o) {
                        return o.getZIndex();
                    })
                );
                let maxImageZIndex = Math.max.apply(
                    Math,
                    layers.map(function (o) {
                        return o instanceof VectorLayer || o instanceof VectorImageLayer ? 0 : o.getZIndex();
                    })
                );
                let vectorLayers = layers.filter(x => x instanceof VectorLayer || x instanceof VectorImageLayer);
                vectorLayers.map(vLayer => {
                    if (vLayer.getZIndex() <= maxImageZIndex) {
                        vLayer.setZIndex(maxZIndex + 1);
                    }
                });
            }
        }
    }

    addDefaultLayers(layers) {
        return new Promise((resolve, reject) => {
            try {
                if (layers) {
                    layers.map(config => {
                        if (!this.getLayer(config.id)) {
                            // let resource = config.resource ?? {};
                            // config.name = config.name;
                            // config.attributions = resource.attributions ? resource.attributions :
                            //     (config.attributions ? config.attributions :
                            //         (config.attribution ? config.attribution : ''));
                            // delete config.attribution;
                            // config.minZoom = config.minZoom ?? undefined;
                            // delete config.minzoom;
                            // config.maxZoom = config.maxZoom ?? undefined;
                            // delete config.maxzoom;
                            // config.zIndex = config.zIndex ?? config.zindex ?? undefined;
                            // delete config.order;
                            // delete config.zindex;

                            if (!config.code) {
                                config.code = getLayerId({ name: config.name });
                            }

                            // if(!config.url){
                            //     config.url = resource?.service?.url ?? null;
                            // }
                            config.url = formatString(config.url, config, "{", "}");

                            // if(!config.format){
                            //     if(resource.formats) {
                            //         config.format = isArray(resource.formats) && resource.formats.length ? (resource.formats[0].name ? resource.formats[0].name : resource.formats[0]) : 'image/png';
                            //     }
                            // }
                            // config.srs = config.srs ? config.srs : resource.crs ? resource.crs : resource.srid ? resource.srid : null;
                            // delete config.crs;
                            // delete config.srid;

                            // let service = config.param_service ?
                            //     config.param_service :
                            //     (config.service ? config.service :
                            //         resource.service ? resource.service.service.code : 'wms');

                            // if(config.type === undefined){
                            //     config.type = service.toLowerCase();
                            // }

                            let _olayer = null;
                            switch (config.type) {
                                case "bingmaps":
                                    _olayer = this.createBingMapsLayer({ ...config, ...{ key: config.apikey } });
                                    break;
                                case "osm":
                                    _olayer = this.createOSMLayer(config);
                                    break;
                                case "xyz":
                                    _olayer = this.createXYZLayer(config);
                                    break;
                                case "vectortile":
                                    _olayer = this.createVectorTileLayer(config);
                                    break;
                                case "wms":
                                    config.param_service = config.type;
                                    config.param_layers = config.layers;
                                    config.param_format = config.format;
                                    config.projection = config.srs;
                                    _olayer = this.createTileLayer(config);
                                    break;
                                case "wmts":
                                    let projection = this.getProjection();
                                    let projCode = projection.getCode();
                                    let projMetersPerUnit = projection.getMetersPerUnit();
                                    if (!config.matrixSet && config.tileMatrixSet) {
                                        let tilematrixset = config.tileMatrixSet.find(ms => {
                                            return getEPSGCode(ms.supported_crs) == projCode;
                                        });

                                        if (tilematrixset) {
                                            config.srs = projCode;
                                            config.matrixSet = tilematrixset.identifier;
                                            let tilematrixs = tilematrixset.tilematrixs;
                                            let resolutions = [];
                                            let matrixIds = [];
                                            let origins = [];
                                            let sizes = [];
                                            let tileSizes = [];
                                            tilematrixs.sort((a, b) => (parseFloat(a.scale_denominator) > parseFloat(b.scale_denominator) ? -1 : 1));
                                            tilematrixs.forEach(matrix => {
                                                matrixIds.push(matrix.identifier);
                                                resolutions.push((parseFloat(matrix.scale_denominator) * 0.00028) / projMetersPerUnit);
                                                origins.push(matrix.top_left_corner.split(" ").map(n => parseFloat(n)));
                                                sizes.push([matrix.matrix_width, matrix.matrix_height]);
                                                tileSizes.push([matrix.tile_width, matrix.tile_height]);
                                            });
                                            config.resolutions = resolutions;
                                            config.matrixIds = matrixIds;
                                            config.origins = origins;
                                            config.sizes = sizes;
                                            config.tileSizes = tileSizes;
                                        }
                                    }
                                    config.srs = projCode;
                                    // config.requestEncoding = config.request_encoding || 'KVP';
                                    config.layer = config.layers;
                                    _olayer = this.createWMTSLayer(config);
                                    break;
                                default:
                                // code block
                            }

                            if (_olayer) {
                                let _layer = this.setLayer(config.code, _olayer);
                                this.addlayerToMap({ layer: _layer });
                            }
                        }
                    });
                }
                resolve();
            } catch (e) {
                reject(e);
            }
        });
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que criar o mapa
    createMap(target, config = {}) {
        return new Promise((resolve, reject) => {
            Object.freeze(config);
            this.setTarget(target);

            if (this.getOLMap() == null) {
                try {
                    let configView = {};
                    configView["projection"] = config.projection ? config.projection : "EPSG:3857";

                    if (config.extent) {
                        let extent = config.extent;
                        configView["extent"] = [extent.minx, extent.miny, extent.maxx, extent.maxy];
                    }
                    if (config.center) {
                        let center = config.center;
                        configView["center"] = [center.x, center.y];
                    }

                    configView["constrainResolution"] = false;
                    if (config.resolutions) {
                        configView["resolutions"] = Object.freeze(config.resolutions);
                    } else if (config.scales) {
                        let scales = config.scales;
                        scales.sort((a, b) => (parseFloat(a) > parseFloat(b) ? -1 : 1));
                        let resolutions = [];
                        let projection = this.getProjectionFromCode(configView["projection"]);
                        let projMetersPerUnit = projection.getMetersPerUnit();
                        scales.forEach(scale_denominator => {
                            resolutions.push((parseFloat(scale_denominator) * 0.00028) / projMetersPerUnit);
                        });
                        configView["resolutions"] = resolutions;
                    }
                    if (config.zoom) {
                        configView["zoom"] = config.zoom;
                    }
                    if (config.initialExtent) {
                        this.setInitialExtent(config.initialExtent);
                    }
                    if (config.boundary) {
                        this.setGeomBoundary(config.boundary);
                    }
                    let interactions = defaultInteractions();
                    if (config.interactions && isArray(config.interactions)) {
                        interactions.extend(config.interactions);
                    } else {
                        let interactionsProperties = this.getInteractionsProperties();
                        if (interactionsProperties && (interactionsProperties.spiderfier || interactionsProperties.spiderfier === undefined)) {
                            interactions.extend([new Spiderfier_Interaction(interactionsProperties.spiderfier ? interactionsProperties.spiderfier : {})]);
                        }
                    }

                    let instance = new Map({
                        renderer: 'webgl', // Rendering with WebGL to improve performance
                        target: target,
                        view: new View(configView),
                        controls: [], //defaultControls(),
                        interactions: interactions
                        // {
                        //     projection: etrs,
                        //     extent: config.extent,
                        //     resolutions: resolutions,
                        //     center: this.getCenter(),
                        //     zoom: this.getZoom()
                        // }),
                        // ol.control.defaults().extend([
                        //   new ol.control.Control({
                        //     element:element
                        //   })
                        // ])
                    });

                    // instance.addControl(new Rotate());
                    // instance.addControl(new Attribution({collapsible: false}));
                    this.setOLMap(instance);

                    this.fitMap(1.25);

                    // window._map = instance;
                    resolve({ mapInstance: this, mapOl: instance, baseLayers: config?.baseMaps ?? [] });
                } catch (e) {
                    reject(e);
                }
            }
            resolve({ instance: this, mapOl: null, baseLayers: [] });
        });
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que destroi o mapa
    destroyMap() {
        var map = this.getOLMap();
        if (map) {
            map.getControls()
            .forEach((control, index, object) => {
                if (map.removeControl(control)) {
                    control.setMap(null);
                    control.dispose();
                    control.dispatchEvent(new Event("removed"));
                    map.dispatchEvent(new Event("control-removed", control));
                }
                object.splice(index, 1);
            });

            map.getOverlays()
            .getArray()
            .forEach((overlay, index, object) => {
                if (map.removeOverlay(overlay)) {
                    overlay.setMap(null);
                }
                object.splice(index, 1);
            });

            let layers = map.getLayers().getArray();
            for (let index = layers.length - 1; index >= 0; index--) {
                let layer = layers[index];
                let source = layer.getSource();
                if (source && source.clear) {
                    source.clear();
                }
                source = null;

                layer.setSource(null);
                if (map.removeLayer(layer)) {
                    layer.setMap(null);
                }
                layer = null;
            }
            layers = null;

            for (let name in this.#source) {
                let src = this.#source[name];
                if (src && src.clear) {
                    src.clear();
                }
                this.#source[name] = null;
            }
            for (let name in this.#layer) {
                let lyr = this.#layer[name];
                if (lyr) {
                    lyr.setSource(null);
                    map.removeLayer(lyr);
                }
                this.#layer[name] = null;
            }

            map.setTarget(null);
            map = null;
        }
        this.setOLMap(null);
        this.setTarget(null);
        this.reset();
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que clona o mapa
    cloneMap(map, target, layers, view) {
        return new Promise((resolve, reject) => {
            try {
                let _layers = [];
                let currentLayers = map.getLayers();
                currentLayers.forEach(l => {
                    let _layer = this.cloneLayer(l);
                    if (_layer) _layers.push(_layer);
                });
                // console.log('layers', layers, _layers);
                // let map2 = map.getOLMap();
                // let view2 = new View({
                //     projection: map2.getView().getProjection(),
                //     center: map2.getView().getCenter(),
                //     resolution: map2.getView().getResolution(),
                //     rotation: map2.getView().getRotation(),
                //     maxZoom: map2.getView().getMaxZoom(),
                //     minZoom: map2.getView().getMinZoom()
                // });
                let instance = new Map({
                    target: target,
                    layers: _layers,
                    view: view,
                    controls: defaultControls({ attribution: true, rotate: false }),
                    interactions: defaultInteractions()
                });

                // currentLayers.addEventListener("propertychange", (evt) => {
                //     this.propertychangeClonedMap(evt, instance);
                // }, false);

                resolve(instance);
            } catch (e) {
                reject(e);
            }
        });
    }

    propertychangeClonedMap(evt, instance) {
        // console.log('triggered when layer added or removed')
        if (evt.target instanceof Collection) {
            let printLayers = instance.getLayers();
            printLayers
                .getArray()
                .slice()
                .forEach(l => instance.removeLayer(l));
            evt.target.forEach(layer => {
                let _layer = this.cloneLayer(layer);
                if (_layer) printLayers.push(_layer);
            });
        }
    }

    deleteClonedMap(map) {
        if (map) {
            map.setTarget(null);
            let currentLayers = map.getLayers();
            if (currentLayers) {
                currentLayers.removeEventListener("propertychange", this.propertychangeClonedMap);
            }
        }
    }

    getSize() {
        let map = this.getOLMap();
        if (map) {
            return map.getSize();
        }
        return null;
    }

    setSize(size) {
        let map = this.getOLMap();
        if (map && size) {
            map.setSize(size);
        }
    }

    updateSize() {
        let map = this.getOLMap();
        if (map) {
            map.updateSize();
        }
    }

    refresh(params = { force: true }) {
        let map = this.getOLMap();
        if (map) {
            map.getLayers().forEach(layer => {
                if (layer.getSource() instanceof WMTSTileGrid || layer.getSource() instanceof TileWMS || layer.getSource() instanceof ImageWMS || layer.getSource() instanceof XYZ)
                    layer.getSource().refresh(params);
                // layer.getSource().updateParams({"time": Date.now()});
            });
        }
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que cria botoes do tipo button para colocar no mapa
    createBtn(html, classAdd, title, events) {
        let button = document.createElement("button");
        button.innerHTML = html;
        if (title) {
            button.title = title;
        }

        const cssClasses = this.getControlClassName(classAdd);
        let element = document.createElement("div");
        element.className = cssClasses;
        element.appendChild(button);

        events.map(evt => {
            button.addEventListener(evt.type, evt.event, false);
        });

        let control = new Control({
            element: element
        });
        return control;
    }

    //////////////////////////////////////////////////////////////////
    // Metodo que cria botoes do tipo img para colocar no mapa
    createBtnImg(src, classAdd, title, events, label = null, classImg = "", toggle = false) {
        const cssClasses = this.getControlClassName(classAdd);
        let el = document.createElement("div");
        el.className = cssClasses;

        let btn = this.createHtmlBtnImg(title, label, src, null, classImg, events, toggle);
        el.appendChild(btn);

        let control = new Control({
            element: el,
            target: null
        });
        return control;
    }

    createBtnIcon(icon, classAdd, title, events, label = null, classImg = "", toggle = false) {
        const cssClasses = this.getControlClassName(classAdd);
        let el = document.createElement("div");
        el.className = cssClasses;

        let btn = this.createHtmlBtnImg(title, label, null, icon, classImg, events, toggle);
        el.appendChild(btn);

        let control = new Control({
            element: el,
            target: null
        });
        return control;
    }

    createHtmlBtnImg(title, label, src, icon = null, cls = "", events = null, toggle = false) {
        label = this.#isMobile ? null : label;

        let button = document.createElement("button");
        button.setAttribute("type", "button");
        button.setAttribute("autocomplete", "off");
        button.setAttribute("class", label ? "w-label" : "");
        if (title) {
            button.title = title;
        }

        const item = document.createElement("label");
        item.setAttribute("class", "btn");

        if(src) {
            let img = document.createElement("img");
            img.setAttribute("class", cls);
            img.setAttribute("src", src);
            item.append(img);
        }else if(icon){
            let _icon = document.createElement("i");
            _icon.setAttribute("class", icon);
            item.append(_icon);
        }

        if (label) {
            let txt = document.createElement("p");
            txt.textContent = label === true ? title : label;
            item.append(txt);
        }
        button.appendChild(item);

        const CLASS_NAME_ACTIVE = "active";
        const SELECTOR_DATA_TOGGLES = '[data-toggle="buttons"]';
        const SELECTOR_DATA_TOGGLE = '[data-toggle="button"]';
        if (toggle) {
            button.setAttribute("data-toggle", "button");
            button.setAttribute("aria-pressed", false);
            button.addEventListener(
                "click",
                e => {
                    let target = event.target;
                    const button = target.closest(SELECTOR_DATA_TOGGLE);
                    if (button) {
                        const activeElement = button.classList.contains(CLASS_NAME_ACTIVE);
                        if (!(button.hasAttribute("disabled") || button.classList.contains("disabled"))) {
                            button.setAttribute("aria-pressed", !button.classList.contains(CLASS_NAME_ACTIVE));

                            if (activeElement) {
                                button.classList.remove(CLASS_NAME_ACTIVE);
                                e.preventDefault();
                                e.stopPropagation();
                                // e.stopImmediatePropagation();
                            } else {
                                button.classList.add(CLASS_NAME_ACTIVE);
                            }
                        }
                    }
                },
                false
            );
        }

        if (events && Object.prototype.toString.call(events) === "[object Array]") {
            events.map(evt => {
                // button.addEventListener(evt.type, evt.event, false);
                button.addEventListener(
                    evt.type,
                    e => {
                        let activeElement = true;
                        let target = event.target;
                        const button = target.closest(SELECTOR_DATA_TOGGLE);
                        if (button) {
                            activeElement = button.classList.contains(CLASS_NAME_ACTIVE);
                        }
                        evt.event(e, activeElement);
                    },
                    false
                );

                // button.addEventListener(evt.type, (e) => {
                //     e.stopPropagation();
                //     e.preventDefault();
                //     console.log(':', 'click BUTTON');
                //     // evt.event();
                // });
                // evt.stopPropagation();
            }, false);
        }

        return button;
    }

    getControlClassName(cls) {
        return CLASS_UNSELECTABLE + " " + CLASS_CONTROL + " " + (cls ? cls : "");
    }

    cloneLayer(layer) {
        if (!layer) {
            return;
        }

        let props = layer.getProperties() || {};
        delete props.map;
        let _layer = null;
        switch (layer.constructor.name) {
            case "TileLayer":
                _layer = new TileLayer(props);
                break;
            case "VectorLayer":
                _layer = new VectorLayer(props);
                // _layer = new TileLayer(props);
                // new VectorLayer({
                //     source: config.source || null,
                //     visible: config.visible,
                //     zIndex: config.zIndex,
                //     // Layer style. When set to null, only features that have their own style will be rendered.
                //     style:
                // })
                break;
            case "VectorImageLayer":
                _layer = new VectorImageLayer(props);
                break;
            default:
                break;
        }
        return _layer;
    }
}
